import React, {
  useState,
  useEffect,
  createContext,
  useContext,
  ReactNode,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import useToast from "../../hooks/useToast";
import { setBuildingData } from "../../redux/buildings/action";
import { setUserInfo } from "../../redux/user/action";
import BuildingService from "./BuildingService";
import Network from "../../utils/Network";

interface BuildingContextType {
  buildings: any[];
  loading: boolean;
  detailLoading: boolean;
  detailDialog: any;
  publicLoading: boolean | number;
  confirmDialog: any;
  getBuildings: () => Promise<void>;
  getBuilding: (id: number | string, item?: any) => Promise<void>;
  upgradeBuilding: (id: number | string) => Promise<void>;
  instantUpgradeBuilding: (id: number | string) => Promise<void>;
  getInstantUpgradePrice: (id: number | string) => Promise<any>;
  repairBuilding: (id: number | string, options?: any) => Promise<boolean>;
  collectFromBuilding: (id: number | string) => Promise<void>;
  buildBuilding: (id: number | string) => Promise<void>;
  openConfirmDialog: (options: any) => void;
  closeDetailDialog: () => void;
  getUserInfo: () => Promise<void>;
  closeConfirmDialog: () => void;
}

const BuildingContext = createContext<BuildingContextType | undefined>(
  undefined
);

interface BuildingProviderProps {
  children: ReactNode;
}

export const BuildingProvider: React.FC<BuildingProviderProps> = ({
  children,
}) => {
  // Get initial building state from Redux
  const reduxBuildings = useSelector((state: any) => state.buildings);
  const [buildings, setBuildings] = useState<any[]>(reduxBuildings || []);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [publicLoading, setPublicLoading] = useState<boolean | number>(false);
  const [detailDialog, setDetailDialog] = useState<any>({
    open: false,
    item: null,
    buildable: false,
  });
  const [confirmDialog, setConfirmDialog] = useState<any>({
    open: false,
    submit: null,
    title: "",
    msg: "",
  });

  const openToast = useToast();
  const dispatch = useDispatch();
  const axios = new Network();

  // Initialize buildings from Redux when component mounts
  useEffect(() => {
    if (
      reduxBuildings &&
      Array.isArray(reduxBuildings) &&
      reduxBuildings.length > 0
    ) {
      setBuildings(reduxBuildings);
      setLoading(false);
    }
  }, [reduxBuildings]);

  const getUserInfo = async () => {
    try {
      const response = await axios.CallApi({
        url: "/player",
        method: "get",
      });

      if (response.ok) {
        dispatch(setUserInfo(response.data));
      }
    } catch (error) {
      console.error("Error fetching user info:", error);
    }
  };

  const getBuildings = async () => {
    setLoading(true);
    try {
      const response = await BuildingService.getAllBuildings();
      if (response.success && response.data) {
        setBuildings(response.data);
        dispatch(setBuildingData(response.data));
      } else {
        openToast({ ok: false, message: response.message });
      }
    } catch (error) {
      console.error("Error fetching buildings:", error);
      openToast({ ok: false, message: "Failed to fetch buildings" });
    } finally {
      setLoading(false);
    }
  };

  const getBuilding = async (id: number | string, item = {}) => {
    if (!id && !parseInt(id?.toString())) {
      setDetailDialog((prev) => ({
        ...prev,
        open: true,
        item: { id: id, ...item },
        buildable: true,
      }));
      return;
    }

    setDetailDialog((prev) => ({
      ...prev,
      open: true,
    }));

    setDetailLoading(true);
    try {
      const response = await BuildingService.getBuildingDetail(id);
      openToast(response);

      // Handle different response formats
      if ("ok" in response && response.ok && response.data) {
        const buildingData = {
          ...item,
          id: id,
          ...response.data,
          buildingId: (response.data as any).buildingId || (item as any).id,
        };

        setDetailDialog((prev) => ({
          ...prev,
          open: true,
          item: buildingData,
          buildable: false,
        }));
      } else if ("success" in response && !response.success) {
        // Handle case where response has success property instead of ok
        console.error("Building detail fetch failed:", response.message);
      }
    } catch (error) {
      console.error("Error fetching building details:", error);
    } finally {
      setDetailLoading(false);
    }
  };

  const upgradeBuilding = async (id: number | string) => {
    setPublicLoading(true);
    try {
      const response = await BuildingService.upgradeBuilding(id);
      openToast(response);

      if ("ok" in response && response.ok) {
        setConfirmDialog((prev) => ({
          ...prev,
          open: false,
        }));
        setDetailDialog((prev) => ({
          ...prev,
          open: false,
        }));
        setPublicLoading(false);

        await getBuildings();
        await getUserInfo();
      }
    } catch (error) {
      console.error("Error upgrading building:", error);
    } finally {
      setPublicLoading(false);
    }
  };

  const instantUpgradeBuilding = async (id: number | string) => {
    setPublicLoading(true);
    try {
      const response = await axios.CallApi({
        url: `/building/${id}/instantupgrade`,
        method: "get",
      });
      openToast(response);

      if ("ok" in response && response.ok) {
        setConfirmDialog((prev) => ({
          ...prev,
          open: false,
        }));
        setDetailDialog((prev) => ({
          ...prev,
          open: false,
        }));

        await getBuildings();
        await getUserInfo();
      }
    } catch (error) {
      console.error("Error instantly upgrading building:", error);
    } finally {
      setPublicLoading(false);
    }
  };

  const getInstantUpgradePrice = async (id: number | string) => {
    try {
      const response = await axios.CallApi({
        url: `/building/${id}/instantupgradeprice`,
        method: "get",
      });
      
      if ("ok" in response && response.ok) {
        return response.data;
      }
      return null;
    } catch (error) {
      console.error("Error getting instant upgrade price:", error);
      return null;
    }
  };

  const repairBuilding = async (
    id: number | string,
    options?: {
      clegUse: number | string;
      ironUse: number | string;
      stoneUse: number | string;
    }
  ) => {
    setPublicLoading(true);
    try {
      const response = await BuildingService.repairBuilding(id, options);
      openToast(response);

      if ("ok" in response && response.ok) {
        setConfirmDialog((prev) => ({
          ...prev,
          open: false,
        }));
        setDetailDialog((prev) => ({
          ...prev,
          open: false,
        }));
        setPublicLoading(false);
        await getBuildings();
        await getUserInfo();
        return true;
      }
      // CallApi resolves on failure too, so report it back instead of
      // letting the caller treat every settled promise as a success.
      return false;
    } catch (error) {
      console.error("Error repairing building:", error);
      return false;
    } finally {
      setPublicLoading(false);
    }
  };

  const collectFromBuilding = async (id: number | string) => {
    setPublicLoading(parseInt(id?.toString()));
    try {
      const response = await BuildingService.collectFromBuilding(id);
      openToast(response);

      if ("ok" in response && response.ok) {
        setConfirmDialog((prev) => ({
          ...prev,
          open: false,
        }));
        setDetailDialog((prev) => ({
          ...prev,
          open: false,
        }));

        setPublicLoading(false);
        await getUserInfo();
        await getBuildings();
      }
    } catch (error) {
      console.error("Error collecting from building:", error);
    } finally {
      setPublicLoading(false);
    }
  };

  const buildBuilding = async (id: number | string) => {
    setPublicLoading(true);
    try {
      const response = await BuildingService.buildBuilding(id);
      openToast(response);

      if ("ok" in response && response.ok) {
        setConfirmDialog((prev) => ({
          ...prev,
          open: false,
        }));
        await getBuildings();
        await getUserInfo();
      } else {
        // If building fails, still close the dialog
        setConfirmDialog((prev) => ({
          ...prev,
          open: false,
        }));
      }
    } catch (error) {
      console.error("Error building:", error);
      // Close dialog even on error
      setConfirmDialog((prev) => ({
        ...prev,
        open: false,
      }));
    } finally {
      setPublicLoading(false);
    }
  };

  const openConfirmDialog = (options: any) => {
    setConfirmDialog({
      open: true,
      ...options,
    });
  };

  const closeDetailDialog = () => {
    setDetailDialog((prev) => ({
      ...prev,
      open: false,
    }));
  };

  const closeConfirmDialog = () => {
    setConfirmDialog((prev) => ({
      ...prev,
      open: false,
    }));
  };

  const contextValue: BuildingContextType = {
    buildings,
    loading,
    detailLoading,
    detailDialog,
    publicLoading,
    confirmDialog,
    getBuildings,
    getBuilding,
    upgradeBuilding,
    instantUpgradeBuilding,
    getInstantUpgradePrice,
    repairBuilding,
    collectFromBuilding,
    buildBuilding,
    openConfirmDialog,
    closeDetailDialog,
    getUserInfo,
    closeConfirmDialog,
  };

  return (
    <BuildingContext.Provider value={contextValue}>
      {children}
    </BuildingContext.Provider>
  );
};

export const useBuildingStore = () => {
  const context = useContext(BuildingContext);
  if (context === undefined) {
    throw new Error("useBuildingStore must be used within a BuildingProvider");
  }
  return context;
};
